import { UserProfile } from "@/services/authService";
import type { HotelSubscriptionFeature } from "@/types/hotel-subscription";
import { Role } from "@/types/user";
import { STANDARD_CORE_PLAN_DISPLAY_FEATURES } from "@/utils/subscriptionFeatureDisplay";
import type { FrontdeskBlock } from "@/constants/frontdeskPermissions";

export const PlatformRole = {
  SUPER_ADMIN: "SUPER_ADMIN",
  GUESTERIA_ADMIN: "GUESTERIA_ADMIN",
} as const;

export const COMMUNICATION_INTEGRATION_ROLES = [
  PlatformRole.SUPER_ADMIN,
  Role.TENANT_ADMIN,
  Role.HOTEL_ADMIN,
] as const;

export const COMMUNICATION_AUDIT_ROLES = [
  ...COMMUNICATION_INTEGRATION_ROLES,
  Role.FRONT_DESK,
] as const;

/** Roles the PMS integration endpoints accept; anyone else only gets 403s. */
export const PMS_INTEGRATION_ROLES = [
  PlatformRole.SUPER_ADMIN,
  Role.TENANT_ADMIN,
  Role.HOTEL_ADMIN,
] as const;

export type PlatformRole = (typeof PlatformRole)[keyof typeof PlatformRole];
export type AppRole = Role | PlatformRole | "GUEST" | "SYSTEM";
export type PlatformPermissionKey = `platform:${string}:${string}`;

export type AccessFeatureValue = boolean | number | "unlimited" | null;

export type AccessFeatureMap = Record<string, AccessFeatureValue>;
export type CorePlatformFeatureKey =
  (typeof STANDARD_CORE_PLAN_DISPLAY_FEATURES)[number]["key"];

export const CANONICAL_BOOLEAN_FEATURE_KEYS = [
  "hotel_brand_settings",
  "hotel_brands",
  "amenities",
  "feedbacks",
  "checkin_checkout",
  "hotel_services",
  "guesteria_partners",
  "staff_operations",
  "requests_system",
  "guest_communication",
  "internal_chat",
  "whatsapp_integration",
  "ai_assistant",
] as const;

export const CANONICAL_NUMERIC_FEATURE_KEYS = [
  "hotels_limit",
  "rooms_limit",
] as const;

export const CANONICAL_FEATURE_KEYS = [
  ...CANONICAL_BOOLEAN_FEATURE_KEYS,
  ...CANONICAL_NUMERIC_FEATURE_KEYS,
] as const;

export type CanonicalBooleanFeatureKey =
  (typeof CANONICAL_BOOLEAN_FEATURE_KEYS)[number];
export type CanonicalNumericFeatureKey =
  (typeof CANONICAL_NUMERIC_FEATURE_KEYS)[number];
export type CanonicalFeatureKey = (typeof CANONICAL_FEATURE_KEYS)[number];
export type NumericFeatureLimit = number | "unlimited" | null;
export type CanonicalFeatureType = "BOOLEAN" | "NUMBER";

export interface CanonicalFeatureDefinition {
  key: CanonicalFeatureKey;
  title: string;
  type: CanonicalFeatureType;
}

interface CapabilityRule {
  requiredFeature?: CanonicalBooleanFeatureKey;
  requiredPermissions?: readonly string[];
}

export type CapabilityKey =
  | "canViewBilling"
  | "canManageBilling"
  | "canUseCustomPages"
  | "canManageFaqs"
  | "canManageCannedResponses"
  | "canManageServices"
  | "canUseFeedbacks"
  | "canManageStaff"
  | "canUseCheckinCheckout"
  | "canUseGuesteriaPartners"
  | "canManageStaffOperations"
  | "canUseRequestsSystem"
  | "canUseGuestCommunication"
  | "canUseInternalChat"
  | "canUseWhatsappIntegration"
  | "canUseAiAssistant";

export type CapabilityMap = Record<CapabilityKey, boolean>;

export const CANONICAL_FEATURE_DEFINITIONS: readonly CanonicalFeatureDefinition[] =
  [
    {
      key: "hotel_brand_settings",
      title: "Hotel Brand Settings",
      type: "BOOLEAN",
    },
    {
      key: "hotel_brands",
      title: "Hotel Brands",
      type: "BOOLEAN",
    },
    {
      key: "amenities",
      title: "Amenities",
      type: "BOOLEAN",
    },
    {
      key: "feedbacks",
      title: "Feedback",
      type: "BOOLEAN",
    },
    {
      key: "checkin_checkout",
      title: "Check-in / Check-out",
      type: "BOOLEAN",
    },
    {
      key: "hotel_services",
      title: "Hotel Services",
      type: "BOOLEAN",
    },
    {
      key: "guesteria_partners",
      title: "Guesteria Partners",
      type: "BOOLEAN",
    },
    {
      key: "staff_operations",
      title: "Staff Operations",
      type: "BOOLEAN",
    },
    {
      key: "requests_system",
      title: "Requests System",
      type: "BOOLEAN",
    },
    {
      key: "guest_communication",
      title: "Guest Communication",
      type: "BOOLEAN",
    },
    {
      key: "internal_chat",
      title: "Internal Chat",
      type: "BOOLEAN",
    },
    {
      key: "whatsapp_integration",
      title: "WhatsApp Integration",
      type: "BOOLEAN",
    },
    {
      key: "ai_assistant",
      title: "AI Assistant",
      type: "BOOLEAN",
    },
    // {
    //   key: "hotels_limit",
    //   title: "Hotels Limit",
    //   type: "NUMBER",
    // },
    {
      key: "rooms_limit",
      title: "Rooms Limit",
      type: "NUMBER",
    },
  ] as const;

export const CANONICAL_FEATURE_DEFINITION_BY_KEY: Record<
  CanonicalFeatureKey,
  CanonicalFeatureDefinition
> = Object.fromEntries(
  CANONICAL_FEATURE_DEFINITIONS.map((feature) => [feature.key, feature]),
) as Record<CanonicalFeatureKey, CanonicalFeatureDefinition>;

export function getCanonicalFeatureDefinition(
  key?: string | null,
): CanonicalFeatureDefinition | null {
  if (!key) {
    return null;
  }

  return (
    CANONICAL_FEATURE_DEFINITION_BY_KEY[key as CanonicalFeatureKey] ?? null
  );
}

export function getCanonicalFeatureTitle(
  key?: string | null,
  fallbackTitle?: string | null,
): string {
  const canonicalDefinition = getCanonicalFeatureDefinition(key);
  if (canonicalDefinition) {
    return canonicalDefinition.title;
  }

  const normalizedFallback = fallbackTitle?.trim();
  if (normalizedFallback) {
    return normalizedFallback;
  }

  const normalizedKey = key?.trim();
  if (normalizedKey) {
    return normalizedKey;
  }

  return "—";
}

const CANONICAL_FEATURE_KEY_ALIASES: Record<
  CanonicalFeatureKey,
  readonly string[]
> = {
  hotel_brand_settings: ["hotel_brand_settings"],
  hotel_brands: ["hotel_brands"],
  amenities: ["amenities"],
  feedbacks: ["feedbacks"],
  checkin_checkout: ["checkin_checkout"],
  hotel_services: ["hotel_services", "hotel_services_access"],
  guesteria_partners: ["guesteria_partners"],
  staff_operations: ["staff_operations", "staff", "staff_access"],
  requests_system: ["requests_system"],
  guest_communication: ["guest_communication"],
  internal_chat: ["internal_chat"],
  whatsapp_integration: ["whatsapp_integration"],
  ai_assistant: [
    "ai_assistant",
    "ai_in_chat",
    "ai_in_chats",
    "ai_chat",
    "ai_chats",
    "chat_ai",
  ],
  hotels_limit: ["hotels_limit"],
  rooms_limit: ["rooms_limit"],
};

export interface AccessLimits {
  hotelsLimit: NumericFeatureLimit;
  roomsLimit: NumericFeatureLimit;
}

export type PermissionSet = Set<string>;

const CORE_PLATFORM_FEATURE_KEY_SET = new Set<CorePlatformFeatureKey>(
  STANDARD_CORE_PLAN_DISPLAY_FEATURES.map((feature) => feature.key),
);

const CAPABILITY_RULES: Partial<Record<CapabilityKey, CapabilityRule>> = {
  canUseCheckinCheckout: {
    requiredFeature: "checkin_checkout",
    requiredPermissions: [
      "checkin_checkout:*",
      "checkin_checkout:read",
      "checkin_checkout:update",
    ],
  },
  canManageServices: {
    requiredFeature: "hotel_services",
    requiredPermissions: [
      "hotel_services:*",
      "hotel_services:read",
      "hotel_services:update",
    ],
  },
  canUseFeedbacks: {
    requiredFeature: "feedbacks",
  },
  canUseRequestsSystem: {
    requiredFeature: "requests_system",
    requiredPermissions: ["requests:*", "requests:read", "requests:update"],
  },
  canUseGuestCommunication: {
    requiredFeature: "guest_communication",
    requiredPermissions: [
      "guest_communication:*",
      "guest_communication:read",
      "guest_communication:update",
      "chat:*",
      "chat:read",
      "chat:update",
    ],
  },
  canUseInternalChat: {
    requiredFeature: "internal_chat",
    requiredPermissions: [
      "internal_chat:*",
      "internal_chat:read",
      "internal_chat:update",
      "chat:*",
      "chat:read",
      "chat:update",
    ],
  },
  // The backend tenant-permission catalog has no separate AI or WhatsApp
  // resources. Their paid feature flags are the canonical capability gates;
  // route-level role guards still restrict administrative configuration.
  canUseWhatsappIntegration: {
    requiredFeature: "whatsapp_integration",
  },
  canUseAiAssistant: {
    requiredFeature: "ai_assistant",
  },
};

function createDefaultFeatureMap(): AccessFeatureMap {
  const booleanDefaults = Object.fromEntries(
    CANONICAL_BOOLEAN_FEATURE_KEYS.map((key) => [key, false]),
  );
  const numericDefaults = Object.fromEntries(
    CANONICAL_NUMERIC_FEATURE_KEYS.map((key) => [key, null]),
  );

  return {
    ...booleanDefaults,
    ...numericDefaults,
  };
}

export function toFeatureMap(
  userProfile: UserProfile | null,
): AccessFeatureMap {
  const featureMap = createDefaultFeatureMap();
  if (userProfile?.access?.features == null) {
    return featureMap;
  }

  const providedFeatureKeys = new Set<string>();
  for (const feature of userProfile.access.features) {
    providedFeatureKeys.add(feature.key);
    featureMap[feature.key] = feature.value;
  }

  // Derive "rooms" access only from an explicitly provided rooms_limit.
  // A missing rooms_limit should not make limited plans look unlimited.
  const roomsLimit = featureMap["rooms_limit"];
  if (
    !providedFeatureKeys.has("rooms") &&
    providedFeatureKeys.has("rooms_limit") &&
    (roomsLimit === null ||
      roomsLimit === "unlimited" ||
      (typeof roomsLimit === "number" && roomsLimit > 0))
  ) {
    featureMap["rooms"] = true;
  }

  return featureMap;
}

function hasBooleanFeatureValue(
  featureMap: AccessFeatureMap,
  aliases: readonly string[],
): boolean {
  for (const key of aliases) {
    if (featureMap[key] === true) {
      return true;
    }
  }
  return false;
}

function hasHotelSubscriptionBooleanFeatureValue(
  features: HotelSubscriptionFeature[] | null | undefined,
  aliases: readonly string[],
): boolean {
  if (!Array.isArray(features)) {
    return false;
  }

  return features.some((feature) => {
    if (!aliases.includes(feature.key) || !feature.enabled) {
      return false;
    }

    if (typeof feature.value === "boolean") {
      return feature.value;
    }

    if (typeof feature.value === "string") {
      if (feature.value === "true") {
        return true;
      }

      if (feature.value === "false") {
        return false;
      }
    }

    return true;
  });
}

function getNumericFeatureValue(
  featureMap: AccessFeatureMap,
  aliases: readonly string[],
): NumericFeatureLimit {
  for (const key of aliases) {
    const value = featureMap[key];
    if (value === "unlimited") {
      return value;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }
  return null;
}

type RoleSource = Pick<UserProfile, "role" | "effectiveRole"> | null;

export function isSuperAdminRole(userProfile: RoleSource): boolean {
  return userProfile?.role === PlatformRole.SUPER_ADMIN;
}

export function isGuesteriaAdminRole(userProfile: RoleSource): boolean {
  if (userProfile?.role === PlatformRole.GUESTERIA_ADMIN) {
    return true;
  }
  return userProfile?.effectiveRole?.baseRole === PlatformRole.GUESTERIA_ADMIN;
}

export function isPlatformAdminRole(userProfile: RoleSource): boolean {
  return isSuperAdminRole(userProfile) || isGuesteriaAdminRole(userProfile);
}

export function isTenantAdminRole(userProfile: RoleSource): boolean {
  return (
    userProfile?.role === Role.TENANT_ADMIN ||
    userProfile?.effectiveRole?.baseRole === Role.TENANT_ADMIN
  );
}

export function isFrontDeskRole(userProfile: RoleSource): boolean {
  return (
    userProfile?.role === Role.FRONT_DESK ||
    userProfile?.effectiveRole?.baseRole === Role.FRONT_DESK
  );
}

export function isCustomFrontDeskRole(userProfile: RoleSource): boolean {
  return (
    isFrontDeskRole(userProfile) &&
    userProfile?.effectiveRole?.type === "CUSTOM"
  );
}

export function isHotelScopedSubscriptionRole(
  userProfile: UserProfile | null,
): boolean {
  return (
    hasRole(userProfile, Role.HOTEL_ADMIN) ||
    hasRole(userProfile, Role.FRONT_DESK) ||
    hasRole(userProfile, Role.STAFF)
  );
}

export function hasCorePlatformEnabled(
  userProfile: UserProfile | null,
): boolean {
  return (
    userProfile?.access?.currentPlan?.enableCoreFeatures ??
    userProfile?.access?.enableCoreFeatures ??
    false
  );
}

export function hasHotelBrandSettingsAccess(
  userProfile: UserProfile | null,
): boolean {
  return hasBooleanFeature(toFeatureMap(userProfile), "hotel_brand_settings");
}

export function canAccessSettingsPage(
  userProfile: UserProfile | null,
): boolean {
  if (!userProfile) {
    return false;
  }

  if (isPlatformAdminRole(userProfile)) {
    return true;
  }

  if (isTenantAdminRole(userProfile)) {
    return (
      hasCorePlatformEnabled(userProfile) ||
      hasHotelBrandSettingsAccess(userProfile)
    );
  }

  return hasCorePlatformAccess(userProfile, "hotel_brand_settings");
}

export function canAccessAdminSettingsPage(
  userProfile: UserProfile | null,
): boolean {
  if (!userProfile) {
    return false;
  }

  if (isPlatformAdminRole(userProfile)) {
    return hasPlatformPermission(userProfile, "platform:settings:read");
  }

  if (!isTenantAdminRole(userProfile)) {
    return false;
  }

  return (
    hasTenantPermission(userProfile, "settings:read") ||
    hasTenantPermission(userProfile, "settings:administrate")
  );
}

function hasExplicitFeatureAccess(
  userProfile: UserProfile | null,
  featureKey: string,
): boolean {
  const featureValue = toFeatureMap(userProfile)[featureKey];

  return (
    featureValue === true ||
    featureValue === "unlimited" ||
    (typeof featureValue === "number" && featureValue > 0)
  );
}

export function hasCorePlatformAccess(
  userProfile: UserProfile | null,
  featureKey: CorePlatformFeatureKey,
): boolean {
  if (!CORE_PLATFORM_FEATURE_KEY_SET.has(featureKey)) {
    return false;
  }

  if (isPlatformAdminRole(userProfile) || isTenantAdminRole(userProfile)) {
    return true;
  }

  if (!isHotelScopedSubscriptionRole(userProfile)) {
    return true;
  }

  const access = userProfile?.access;
  if (!(access?.hasActiveSubscription ?? false)) {
    return false;
  }

  if (hasCorePlatformEnabled(userProfile)) {
    return true;
  }

  return hasExplicitFeatureAccess(userProfile, featureKey);
}

export function hasRole(
  userProfile: UserProfile | null,
  role: string,
): boolean {
  return (
    userProfile?.role === role || userProfile?.effectiveRole?.baseRole === role
  );
}

export function isRapidPlan(userProfile: UserProfile | null): boolean {
  return (
    userProfile?.access?.currentPlan?.title?.trim().toLowerCase() === "rapid"
  );
}

export function isStarterPlan(userProfile: UserProfile | null): boolean {
  return (
    userProfile?.access?.currentPlan?.title?.trim().toLowerCase() === "starter"
  );
}

export function hasAnyRole(
  userProfile: UserProfile | null,
  roles: readonly string[],
): boolean {
  return roles.some((role) => hasRole(userProfile, role));
}

function toPermissionKey(entry: unknown): string | null {
  if (typeof entry === "string") {
    const permission = entry.trim();
    return permission.length > 0 ? permission : null;
  }

  if (
    typeof entry === "object" &&
    entry !== null &&
    "name" in entry &&
    typeof entry.name === "string"
  ) {
    const permission = entry.name.trim();
    return permission.length > 0 ? permission : null;
  }

  return null;
}

export function getPermissionSet(
  userProfile: Pick<UserProfile, "permissions"> | null,
): PermissionSet {
  const permissions = new Set<string>();
  const rawPermissions = userProfile?.permissions;

  if (!Array.isArray(rawPermissions)) {
    return permissions;
  }

  for (const entry of rawPermissions) {
    const permissionKey = toPermissionKey(entry);
    if (permissionKey !== null) {
      permissions.add(permissionKey);
    }
  }

  return permissions;
}

type PlatformPermissionSource = Pick<
  UserProfile,
  "permissions" | "role" | "effectiveRole"
> & {
  platformPermissions?: unknown;
  platform_permissions?: unknown;
};

function readStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  return value
    .filter((entry): entry is string => typeof entry === "string")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function getPlatformPermissionArray(
  userProfile: PlatformPermissionSource | null,
): string[] | null {
  if (!userProfile) {
    return null;
  }

  return (
    readStringArray(userProfile.platformPermissions) ??
    readStringArray(userProfile.platform_permissions)
  );
}

export function getPlatformPermissionSet(
  userProfile: PlatformPermissionSource | null,
): PermissionSet {
  const permissions = new Set<string>();
  const rawPlatformPermissions = getPlatformPermissionArray(userProfile);

  for (const permission of rawPlatformPermissions ?? []) {
    permissions.add(permission);
  }

  for (const permission of getPermissionSet(userProfile)) {
    if (permission.startsWith("platform:")) {
      permissions.add(permission);
    }
  }

  return permissions;
}

export function hasKnownPlatformPermissions(
  userProfile: PlatformPermissionSource | null,
): boolean {
  return (
    getPlatformPermissionArray(userProfile) !== null ||
    Array.from(getPermissionSet(userProfile)).some((permission) =>
      permission.startsWith("platform:"),
    )
  );
}

function getPlatformPermissionParts(permission: string): {
  resource: string;
  action: string;
} | null {
  const [namespace, resource, action] = permission.split(":");

  if (namespace !== "platform" || !resource || !action) {
    return null;
  }

  return { resource, action };
}

export function matchesPlatformPermission(
  grantedPermission: string,
  requiredPermission: PlatformPermissionKey,
): boolean {
  if (grantedPermission === requiredPermission) {
    return true;
  }

  if (grantedPermission === "platform:*:*") {
    return true;
  }

  const required = getPlatformPermissionParts(requiredPermission);
  if (!required) {
    return false;
  }

  if (grantedPermission === `platform:${required.resource}:*`) {
    return true;
  }

  return (
    required.action === "read" &&
    grantedPermission === `platform:${required.resource}:administrate`
  );
}

export function hasPlatformPermission(
  userProfile: PlatformPermissionSource | null,
  requiredPermission: PlatformPermissionKey,
): boolean {
  if (isSuperAdminRole(userProfile)) {
    return true;
  }

  if (!hasKnownPlatformPermissions(userProfile)) {
    // Native GUESTERIA_ADMIN without custom-role restrictions: full platform access
    return isGuesteriaAdminRole(userProfile);
  }

  const permissionSet = getPlatformPermissionSet(userProfile);

  for (const permission of permissionSet) {
    if (matchesPlatformPermission(permission, requiredPermission)) {
      return true;
    }
  }

  return false;
}

export function hasPermission(
  userProfile: Pick<UserProfile, "permissions"> | null,
  permission: string,
): boolean {
  return getPermissionSet(userProfile).has(permission);
}

export function hasTenantPermission(
  userProfile: UserProfile | null,
  permission: string,
): boolean {
  if (userProfile?.effectiveRole?.type !== "CUSTOM") {
    return isTenantAdminRole(userProfile);
  }

  const tenantPermissions = [
    ...(userProfile?.tenantPermissions ?? []),
    ...getPermissionSet(userProfile),
  ];
  return tenantPermissions.some(
    (p) =>
      p === "*:*" ||
      p === permission ||
      matchesPermissionPattern(permission, p),
  );
}

export function hasFrontdeskBlockPermission(
  userProfile: UserProfile | null,
  block: FrontdeskBlock,
  level: "read" | "administrate" = "read",
): boolean {
  if (!isFrontDeskRole(userProfile) || !isCustomFrontDeskRole(userProfile)) {
    return true;
  }

  if (level === "administrate") {
    return hasTenantPermission(userProfile, `${block}:administrate`);
  }

  return (
    hasTenantPermission(userProfile, `${block}:read`) ||
    hasTenantPermission(userProfile, `${block}:administrate`)
  );
}

export function matchesPermissionPattern(
  permission: string,
  pattern: string,
): boolean {
  if (permission === pattern) {
    return true;
  }

  if (!pattern.endsWith("*")) {
    return false;
  }

  const prefix = pattern.slice(0, -1);
  return permission.startsWith(prefix);
}

export function hasPermissionPattern(
  userProfile: Pick<UserProfile, "permissions"> | null,
  pattern: string,
): boolean {
  const permissionSet = getPermissionSet(userProfile);

  for (const permission of permissionSet) {
    if (matchesPermissionPattern(permission, pattern)) {
      return true;
    }
  }

  return false;
}

export function hasAnyPermission(
  userProfile: Pick<UserProfile, "permissions"> | null,
  permissions: readonly string[],
): boolean {
  if (permissions.length === 0) {
    return true;
  }

  const permissionSet = getPermissionSet(userProfile);
  if (permissionSet.size === 0) {
    return true;
  }

  return permissions.some((pattern) => {
    for (const permission of permissionSet) {
      if (matchesPermissionPattern(permission, pattern)) {
        return true;
      }
    }
    return false;
  });
}

export function hasBooleanFeature(
  featureMap: AccessFeatureMap,
  key: CanonicalBooleanFeatureKey,
): boolean {
  return hasBooleanFeatureValue(featureMap, CANONICAL_FEATURE_KEY_ALIASES[key]);
}

export function hasHotelSubscriptionFeature(
  features: HotelSubscriptionFeature[] | null | undefined,
  key: CanonicalBooleanFeatureKey,
): boolean {
  return hasHotelSubscriptionBooleanFeatureValue(
    features,
    CANONICAL_FEATURE_KEY_ALIASES[key],
  );
}

export function getNumericFeatureLimit(
  featureMap: AccessFeatureMap,
  key: CanonicalNumericFeatureKey,
): NumericFeatureLimit {
  return getNumericFeatureValue(featureMap, CANONICAL_FEATURE_KEY_ALIASES[key]);
}

export function getAccessLimits(userProfile: UserProfile | null): AccessLimits {
  const featureMap = toFeatureMap(userProfile);

  return {
    hotelsLimit: getNumericFeatureLimit(featureMap, "hotels_limit"),
    roomsLimit: getNumericFeatureLimit(featureMap, "rooms_limit"),
  };
}

export function isLimitReached(
  currentUsage: number,
  limit: NumericFeatureLimit,
): boolean {
  if (limit === null || limit === "unlimited") {
    return false;
  }

  return currentUsage >= limit;
}

export function buildCapabilities(
  userProfile: UserProfile | null,
): CapabilityMap {
  if (isPlatformAdminRole(userProfile)) {
    return {
      canViewBilling: true,
      canManageBilling: true,
      canUseCustomPages: true,
      canManageFaqs: true,
      canManageCannedResponses: true,
      canManageServices: true,
      canUseFeedbacks: true,
      canManageStaff: true,
      canUseCheckinCheckout: true,
      canUseGuesteriaPartners: true,
      canManageStaffOperations: true,
      canUseRequestsSystem: true,
      canUseGuestCommunication: true,
      canUseInternalChat: true,
      canUseWhatsappIntegration: true,
      canUseAiAssistant: true,
    };
  }

  const featureMap = toFeatureMap(userProfile);
  const hasActiveRapidPlan =
    (userProfile?.access?.hasActiveSubscription ?? false) &&
    isRapidPlan(userProfile);
  const hasCanonicalFeatureAccess = (
    featureKey: CanonicalBooleanFeatureKey,
  ): boolean =>
    (userProfile?.access?.hasActiveSubscription ?? false) &&
    hasBooleanFeature(featureMap, featureKey);

  const evaluateCapabilityRule = (capability: CapabilityKey): boolean => {
    const rule = CAPABILITY_RULES[capability];
    return (
      rule == null ||
      ((rule.requiredFeature === undefined ||
        hasCanonicalFeatureAccess(rule.requiredFeature)) &&
        (rule.requiredPermissions === undefined ||
          hasAnyPermission(userProfile, rule.requiredPermissions)))
    );
  };
  const canReadFrontdeskBlock = (block: FrontdeskBlock): boolean =>
    hasFrontdeskBlockPermission(userProfile, block);

  const canViewBilling =
    hasTenantPermission(userProfile, "billing:read") ||
    hasTenantPermission(userProfile, "billing:administrate") ||
    (isHotelScopedSubscriptionRole(userProfile) &&
      hasAnyPermission(userProfile, [
        "billing:*",
        "billing:read",
        "billing:administrate",
      ]));
  const canManageBilling =
    hasTenantPermission(userProfile, "billing:administrate") ||
    (isHotelScopedSubscriptionRole(userProfile) &&
      hasAnyPermission(userProfile, ["billing:*", "billing:administrate"]));

  return {
    canViewBilling,
    canManageBilling,
    canUseCustomPages: canReadFrontdeskBlock("frontdesk_hotel_settings"),
    canManageFaqs: canReadFrontdeskBlock("frontdesk_hotel_settings"),
    canManageCannedResponses: canReadFrontdeskBlock("frontdesk_hotel_settings"),
    canManageServices:
      canReadFrontdeskBlock("frontdesk_management") &&
      (hasActiveRapidPlan || evaluateCapabilityRule("canManageServices")),
    canUseFeedbacks:
      canReadFrontdeskBlock("frontdesk_inbox") &&
      evaluateCapabilityRule("canUseFeedbacks"),
    canManageStaff: canReadFrontdeskBlock("frontdesk_management"),
    canUseCheckinCheckout:
      canReadFrontdeskBlock("frontdesk_guests") &&
      evaluateCapabilityRule("canUseCheckinCheckout"),
    // Rapid is partner-focused: its Services page shows the partner catalog,
    // and its Partners settings remain available without optional overrides.
    canUseGuesteriaPartners:
      hasActiveRapidPlan || hasCanonicalFeatureAccess("guesteria_partners"),
    canManageStaffOperations:
      canReadFrontdeskBlock("frontdesk_management") &&
      hasCanonicalFeatureAccess("staff_operations"),
    canUseRequestsSystem:
      canReadFrontdeskBlock("frontdesk_inbox") &&
      evaluateCapabilityRule("canUseRequestsSystem"),
    canUseGuestCommunication:
      canReadFrontdeskBlock("frontdesk_inbox") &&
      evaluateCapabilityRule("canUseGuestCommunication"),
    canUseInternalChat:
      canReadFrontdeskBlock("frontdesk_management") &&
      evaluateCapabilityRule("canUseInternalChat"),
    canUseWhatsappIntegration: evaluateCapabilityRule(
      "canUseWhatsappIntegration",
    ),
    canUseAiAssistant: evaluateCapabilityRule("canUseAiAssistant"),
  };
}
